import Card from '../../components/card/Card';
import Collapsable from '../../components/collapsable/Collapsable';
import Divider from '../../components/divider/Divider';
import Table from '../../components/table/table';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Span from '../../components/texts/span/Span';
import Tooltip from '../../components/tooltip/Tooltip';
import { formatCurrency } from '../../utils/Masks';
import styled from 'styled-components';
import { useClientTheme } from '../../context/ThemeContext';

const ResponsiveGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 12px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const ResponsiveTableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  
  @media (max-width: 768px) {
    /* En móvil, permitir scroll horizontal para las tablas */
    -webkit-overflow-scrolling: touch;
  }
  
  /* Estilos para las tablas internas */
  .rdt_Table {
    min-width: 300px !important;
    
    @media (max-width: 768px) {
      min-width: 250px !important;
    }
  }
  
  .rdt_TableCol {
    min-width: 120px !important;
    
    @media (max-width: 768px) {
      min-width: 100px !important;
    }
  }
`;

interface FilePreviewProps {
  riskData: any;
  totals: any;
  withCollapsables?: boolean;
  isPdfMode?: boolean;
}

const BalanceTable: React.FC<FilePreviewProps> = ({
  riskData,
  totals,
  withCollapsables = false,
  isPdfMode = false,
}) => {
  const { clientTheme } = useClientTheme();
  const transformObject = (obj: any): any => {
    if (!obj || typeof obj !== 'object') {
      return [];
    }
    return Object.entries(obj).map(([key, value]) => {
      return typeof value === 'object' && value !== null
        ? { category: key, values: transformObject(value) }
        : { label: key, value };
    });
  };

  const transformedData = riskData ? transformObject(riskData) : [];
  const translations = {
    current_assets: {
      current_investments: 'Inversiones corrientes PF',
      cash_and_cash_equivalents: 'Disponibilidades',
      trade_receivables_short_term: 'Créditos por ventas CP',
      other_receivables: 'Otros créditos',
      inventory_short_term: 'Bienes de cambio - CP',
      other_current_assets: 'Otros activos corrientes - CP',
    },
    non_current_assets: {
      trade_receivables_long_term: 'Créditos por ventas LP',
      other_receivables: 'Otros créditos',
      permanent_investments: 'Inversiones permanentes',
      property_plant_and_equipment: 'Bienes de uso',
      other_non_current_assets: 'Otros activos no corrientes',
    },
    current_liabilities: {
      trade_payables_short_term: 'Deudas comerciales CP',
      bank_and_financial_debt_short_term: 'Deuda bancaria y financiera CP',
      social_security_debt_short_term: 'Deudas cargas sociales CP',
      tax_liabilities_short_term: 'Cargas fiscales CP',
      other_debts_provisions: 'Otras deudas - provisiones',
      projections: 'Proyecciones',
      other_liabilities: 'Otros pasivos',
    },
    non_current_liabilities: {
      non_current_liabilities: 'Pasivo no corriente',
      trade_payables_long_term: 'Deudas comerciales LP',
      bank_and_financial_debt_long_term: 'Deuda bancaria y financiera LP',
      social_security_debt_long_term: 'Cargas sociales LP',
      tax_liabilities_long_term: 'Cargas fiscales LP',
      other_long_term_liabilities: 'Otros pasivos LP',
    },
    net_worth: {
      capital: 'Capital',
      other_components: 'Otros',
    },
    gross_result: {
      agricultural_production: 'Producción agropecuaria',
      production_cost: 'Costo de producción',
      sales: 'Ventas',
      cost_of_sales: 'Costo de ventas',
    },
    operating_result: {
      commercialization_expenses: 'Gastos de comercialización',
      administrative_expenses: 'Gastos de administración',
      other_income: 'Otros ingresos',
      other_expenses: 'Otros egresos',
    },
    ordinary_result: {
      financial_expenses: 'Gastos financieros',
      exchange_difference_and_holding_result: 'Dif. de cambio + res. tenencia',
    },
    result_before_taxes: {
      extraordinary_income: 'Ingresos extraordinarios',
      extraordinary_expenses: 'Egresos extraordinarios',
      total: 'Total',
    },
    taxes: {
      income_tax: 'Impuesto a las ganancias',
    },
  };

  const getTranslation = (parent: string, label: string): string => {
    return (translations as any)[parent]?.[label] || label;
  };

  const getColumns = (
    category: string,
    firstHeader: string = '',
    secondHeader: string = ''
  ) => {
    const valueHeader = secondHeader ? '$ ' + secondHeader : '';
    return [
      {
        name: firstHeader,
        selector: (row: any) => (
          <Card>
            <P type='4' variant='neutral'>
              {getTranslation(category, row.label)}
            </P>
          </Card>
        ),
        grow: 2,
      },
      {
        name: valueHeader,
        selector: (row: any) => (
          <Card>
            <Tooltip position='top' msg={row.value}>
              <Span type='4' variant='neutral'>
                $ {formatCurrency(row.value ?? 0, true)}
              </Span>
            </Tooltip>
          </Card>
        ),
        right: true,
      },
    ];
  };

  const renderResult = (
    category: string,
    tableTitle: string,
    totalTitle: string,
    data: any,
    isWeak: boolean = false
  ) => {
    return (
      <Card>
        <Card display='flex' alignItems='end' margin={{ bottom: '4' }}>
          <Card width='50%'>
            <ResponsiveTableWrapper>
              <Table
                columns={getColumns(category, tableTitle, data)}
                tableData={{ size: 10, totalElements: 10 }}
                data={
                  transformedData.find((item: any) => item.category === category)
                    ?.values
                }
                variant='weak'
              />
            </ResponsiveTableWrapper>
          </Card>
          <Card width='50%'>
            <H
              type='4'
              withMargin={false}
              variant={isWeak ? 'neutral' : 'primary'}
              modifier='strong'
              align='right'
            >
              {totalTitle}:
            </H>
            <H
              type='4'
              withMargin={false}
              variant={isWeak ? 'neutral' : 'primary'}
              modifier='strong'
              align='right'
              weight='bolder'
            >
              {data}
            </H>
          </Card>
        </Card>
        <Divider />
      </Card>
    );
  };

  return (
    <Card display='grid' gap='20px'>
      {withCollapsables ? (
        <Collapsable
          startOpened={isPdfMode ? true : false}
          forceOpen={isPdfMode}
          hideToggle={isPdfMode}
          title='Activo'
          rightTitle={totals.total_current}
          titleVariant='neutral-1'
          titleModifier='strong'
          rightTitleVariant='neutral-1'
          rightTitleModifier='strong'
          children={
            <ResponsiveGrid>
              <ResponsiveTableWrapper>
                <Table
                  columns={getColumns(
                    'current_assets',
                    'Activo corriente',
                    totals.current_assets
                  )}
                  tableData={{ size: 10, totalElements: 10 }}
                  data={
                    transformedData.find(
                      (item: any) => item.category === 'current_assets'
                    )?.values
                  }
                  variant='weak'
                />
              </ResponsiveTableWrapper>
              <ResponsiveTableWrapper>
                <Table
                  columns={getColumns(
                    'non_current_assets',
                    'Activo no corriente',
                    totals.non_current_assets
                  )}
                  tableData={{ size: 10, totalElements: 10 }}
                  data={
                    transformedData.find(
                      (item: any) => item.category === 'non_current_assets'
                    )?.values
                  }
                  variant='weak'
                />
              </ResponsiveTableWrapper>
            </ResponsiveGrid>
          }
        />
      ) : (
        <Card>
          <Card display='flex' justifyContent='space-between'>
            <H type='4' variant='secondary-2' weight='bold'>
              Activo
            </H>
            <H type='4' variant={clientTheme ? 'primary' : 'success'} weight='bolder'>
              $ {totals.total_current}
            </H>
          </Card>
          <ResponsiveGrid>
            <ResponsiveTableWrapper>
              <Table
                columns={getColumns(
                  'current_assets',
                  'Activo corriente',
                  totals.current_assets
                )}
                tableData={{ size: 10, totalElements: 10 }}
                data={
                  transformedData.find(
                    (item: any) => item.category === 'current_assets'
                  )?.values
                }
                variant='weak'
              />
            </ResponsiveTableWrapper>
            <ResponsiveTableWrapper>
              <Table
                columns={getColumns(
                  'non_current_assets',
                  'Activo no corriente',
                  totals.non_current_assets
                )}
                tableData={{ size: 10, totalElements: 10 }}
                data={
                  transformedData.find(
                    (item: any) => item.category === 'non_current_assets'
                  )?.values
                }
                variant='weak'
              />
            </ResponsiveTableWrapper>
          </ResponsiveGrid>
          <Divider></Divider>
        </Card>
      )}

      {withCollapsables && (
        <Divider
          variant='primary'
          modifier='regular'
          margin={{ top: '2', bottom: '2' }}
        />
      )}

      {withCollapsables ? (
        <Collapsable
          startOpened={isPdfMode ? true : false}
          forceOpen={isPdfMode}
          hideToggle={isPdfMode}
          title='Pasivo'
          rightTitle={totals.total_non_current}
          titleVariant='neutral-1'
          titleModifier='strong'
          rightTitleVariant='neutral-1'
          rightTitleModifier='strong'
          children={
            <ResponsiveGrid>
              <ResponsiveTableWrapper>
                <Table
                  columns={getColumns(
                    'current_liabilities',
                    'Pasivo corriente',
                    totals.current_liabilities
                  )}
                  tableData={{ size: 10, totalElements: 10 }}
                  data={
                    transformedData.find(
                      (item: any) => item.category === 'current_liabilities'
                    )?.values
                  }
                  variant='weak'
                />
              </ResponsiveTableWrapper>
              <ResponsiveTableWrapper>
                <Table
                  columns={getColumns(
                    'non_current_liabilities',
                    'Pasivo no corriente',
                    totals.non_current_liabilities
                  )}
                  tableData={{ size: 10, totalElements: 10 }}
                  data={transformedData
                    .find((item: any) => item.category === 'non_current_liabilities')
                    ?.values.slice(1)}
                  variant='weak'
                />
              </ResponsiveTableWrapper>
            </ResponsiveGrid>
          }
        />
      ) : (
        <Card>
          <Card display='flex' justifyContent='space-between'>
            <H type='4' variant='secondary-2' weight='bold'>
              Pasivo
            </H>
            <H type='4' variant={clientTheme ? 'primary' : 'success'} weight='bolder'>
              $ {totals?.total_non_current || '0,00'}
            </H>
          </Card>
          <ResponsiveGrid>
            <ResponsiveTableWrapper>
              <Table
                columns={getColumns(
                  'current_liabilities',
                  'Pasivo corriente',
                  totals.current_liabilities
                )}
                tableData={{ size: 10, totalElements: 10 }}
                data={
                  transformedData.find(
                    (item: any) => item.category === 'current_liabilities'
                  )?.values
                }
                variant='weak'
              />
            </ResponsiveTableWrapper>
            <ResponsiveTableWrapper>
              <Table
                columns={getColumns(
                  'non_current_liabilities',
                  'Pasivo no corriente',
                  totals.non_current_liabilities
                )}
                tableData={{ size: 10, totalElements: 10 }}
                data={transformedData
                  .find((item: any) => item.category === 'non_current_liabilities')
                  ?.values.slice(1)}
                variant='weak'
              />
            </ResponsiveTableWrapper>
          </ResponsiveGrid>
          <Divider></Divider>
        </Card>
      )}

      {withCollapsables && (
        <Divider
          variant='primary'
          modifier='regular'
          margin={{ top: '2', bottom: '2' }}
        />
      )}

      {withCollapsables ? (
        <Collapsable
          startOpened={isPdfMode ? true : false}
          forceOpen={isPdfMode}
          hideToggle={isPdfMode}
          title='Patrimonio neto'
          rightTitle={totals.total_net_worth}
          titleVariant='neutral-1'
          titleModifier='strong'
          rightTitleVariant='neutral-1'
          rightTitleModifier='strong'
          children={
            <ResponsiveGrid>
              <ResponsiveTableWrapper>
                <Table
                  columns={getColumns('net_worth', 'Patrimonio neto', '')}
                  tableData={{ size: 10, totalElements: 10 }}
                  data={transformedData
                    .find((item: any) => item.category === 'net_worth')
                    ?.values.slice(0, 1)}
                  variant='weak'
                />
              </ResponsiveTableWrapper>
              <ResponsiveTableWrapper>
                <Table
                  columns={getColumns('net_worth', '', totals.total_net_worth)}
                  tableData={{ size: 10, totalElements: 10 }}
                  data={transformedData
                    .find((item: any) => item.category === 'net_worth')
                    ?.values.slice(-1)}
                  variant='weak'
                />
              </ResponsiveTableWrapper>
            </ResponsiveGrid>
          }
        />
      ) : (
        <Card>
          <Card display='flex' justifyContent='space-between'>
            <H type='4' variant='secondary-2' weight='bold'>
              Patrimonio neto
            </H>
            <H type='4' variant={clientTheme ? 'primary' : 'success'} weight='bolder'>
              $ {totals.total_net_worth}
            </H>
          </Card>
          <ResponsiveGrid>
            <ResponsiveTableWrapper>
              <Table
                columns={getColumns('net_worth', 'Patrimonio neto', '')}
                tableData={{ size: 10, totalElements: 10 }}
                data={transformedData
                  .find((item: any) => item.category === 'net_worth')
                  ?.values.slice(0, 1)}
                variant='weak'
              />
            </ResponsiveTableWrapper>
            <ResponsiveTableWrapper>
              <Table
                columns={getColumns('net_worth', '', totals.total_net_worth)}
                tableData={{ size: 10, totalElements: 10 }}
                data={transformedData
                  .find((item: any) => item.category === 'net_worth')
                  ?.values.slice(-1)}
                variant='weak'
              />
            </ResponsiveTableWrapper>
          </ResponsiveGrid>
          <Divider></Divider>
        </Card>
      )}
      {withCollapsables && (
        <Divider
          variant='primary'
          modifier='regular'
          margin={{ top: '2', bottom: '2' }}
        />
      )}
      {withCollapsables ? (
        <Card
          display='flex'
          justifyContent='space-between'
          margin={{ right: '5' }}
        >
          <P type='1' variant='neutral-1' weight='bold' modifier='strong'>
            Total pasivo + Patrimonio neto
          </P>
          <P type='1' variant='neutral-1' weight='bold' modifier='strong'>
            $ {totals.total_net_worth_and_non_current}
          </P>
        </Card>
      ) : (
        <Card display='flex' justifyContent='space-between'>
          <H type='4' variant='secondary-2' weight='bold'>
            Total pasivo + Patrimonio neto
          </H>
          <H type='4' variant={clientTheme ? 'primary' : 'success'} weight='bolder'>
            $ {totals.total_net_worth_and_non_current}
          </H>
        </Card>
      )}
      {withCollapsables && (
        <Divider
          variant='primary'
          modifier='regular'
          margin={{ top: '2', bottom: '2' }}
        />
      )}
      {withCollapsables ? (
        <Collapsable
          startOpened={isPdfMode ? true : false}
          forceOpen={isPdfMode}
          hideToggle={isPdfMode}
          title='Resultados'
          titleVariant='neutral-1'
          titleModifier='strong'
          children={
            <Card>
              <H type='4' variant='neutral-1' weight='bold' modifier='strong'>
                Resultados
              </H>
              {renderResult(
                'gross_result',
                'Resultado bruto',
                'Total resultado bruto',
                totals.gross_result,
                true
              )}
              {renderResult(
                'operating_result',
                'Resultado operativo',
                'Total resultado operativo',
                totals.operating_result,
                true
              )}
              {renderResult(
                'ordinary_result',
                'Resultado ordinario',
                'Total resultado ordinario',
                totals.ordinary_result,
                true
              )}
              {renderResult(
                'result_before_taxes',
                'Resultado antes de impuestos',
                'Total resultado antes de impuestos',
                formatCurrency(totals.result_before_taxes?.total || 0),
                true
              )}
              {renderResult(
                'taxes',
                'Impuesto a las ganancias',
                'Total impuesto a las ganancias',
                totals.taxes,
                true
              )}
            </Card>
          }
        />
      ) : (
        <Card>
          <H type='4' variant='secondary-2' weight='bold'>
            Resultados
          </H>
          {renderResult(
            'gross_result',
            'Resultado bruto',
            'Total resultado bruto',
            totals.gross_result
          )}
          {renderResult(
            'operating_result',
            'Resultado operativo',
            'Total resultado operativo',
            totals.operating_result
          )}
          {renderResult(
            'ordinary_result',
            'Resultado ordinario',
            'Total resultado ordinario',
            totals.ordinary_result
          )}
          {renderResult(
            'result_before_taxes',
            'Resultado antes de impuestos',
            'Total resultado antes de impuestos',
            formatCurrency(totals.result_before_taxes?.total || 0)
          )}
        </Card>
      )}

      {withCollapsables && (
        <Card display='flex' justifyContent='space-between' alignItems='center'>
          <P type='1' variant='primary' weight='bold' modifier='strong'>
            Resultado del ejercicio
          </P>
          <P type='1' variant='primary' weight='bold' modifier='strong'>
            $ {totals.taxes}
          </P>
        </Card>
      )}
    </Card>
  );
};

export default BalanceTable;